import { Logo } from "@/components/ui/logo";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { CreditCard, Home } from "lucide-react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

const navigationItems = [
  { title: "Dashboard", icon: Home, url: "/" },
  { title: "Subscription", icon: CreditCard, url: "/billing" },
];

export const AppSidebar: React.FC<React.ComponentProps<typeof Sidebar>> = ({
  ...props
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActiveItem = (itemUrl: string) => {
    // Handle exact match for root path
    if (itemUrl === "/" && location.pathname === "/") {
      return true;
    }
    // Handle other paths (usage, settings, etc.)
    if (itemUrl !== "/" && location.pathname.startsWith(itemUrl)) {
      return true;
    }
    return false;
  };

  return (
    <Sidebar
      collapsible="icon"
      {...props}
      className="flex flex-col justify-center items-center"
    >
      {/* Enhanced Header */}
      <SidebarHeader className="relative overflow-hidden flex justify-center items-center mb-4">
        <div className="relative flex items-center justify-center px-2 py-4 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-3">
          {/* Regular logo for expanded state */}
          <div className="group-data-[collapsible=icon]:hidden">
            <Logo className="h-7 w-auto" />
          </div>
          {/* Small logo for collapsed state */}
          <div className="hidden group-data-[collapsible=icon]:block">
            <Logo className="h-6 w-auto" collapsed={true} />
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2">
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="flex flex-col gap-3 group-data-[collapsible=icon]:items-center  justify-center w-full">
              {navigationItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActiveItem(item.url)}
                    tooltip={item.title}
                    className={cn(
                      "h-12 w-80 px-8 py-6 rounded-lg transition-all duration-200 group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:justify-center",
                      isActiveItem(item.url) &&
                        "bg-blue-200 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold shadow-sm border border-blue-300 dark:border-blue-800/50",
                      !isActiveItem(item.url) &&
                        "hover:bg-slate-100 dark:hover:bg-slate-800/50",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => navigate(item.url)}
                      className="flex items-center gap-3 w-full group-data-[collapsible=icon]:justify-center cursor-pointer"
                    >
                      <div
                        className={cn(
                          "p-1.5 rounded-md transition-colors group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:bg-transparent",
                          isActiveItem(item.url)
                            ? "bg-blue-200 dark:bg-blue-800/50 group-data-[collapsible=icon]:bg-transparent"
                            : "bg-slate-100 dark:bg-slate-800 group-data-[collapsible=icon]:bg-transparent",
                        )}
                      >
                        <item.icon
                          className={cn(
                            "h-4 w-4",
                            isActiveItem(item.url)
                              ? "text-blue-600 dark:text-blue-400"
                              : "text-slate-600 dark:text-slate-400",
                          )}
                        />
                      </div>
                      <span className="font-medium text-base group-data-[collapsible=icon]:hidden">
                        {item.title}
                      </span>
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
};
